import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useLocation } from "wouter";
import confetti from "canvas-confetti";
import { apiRequest } from "@/lib/queryClient";
import { CheckCircle2, ChevronDown, ChevronUp, Circle, Sparkles, X } from "lucide-react";
import {
  ONBOARDING_BAR_HIDDEN_KEY,
  ONBOARDING_BAR_PREF_EVENT,
  isOnboardingBarHidden,
} from "@/lib/onboarding-bar-pref";

interface ProgressItem {
  id: string;
  label: string;
  done: boolean;
  link?: string;
  hint?: string;
}

interface ProgressResponse {
  items: ProgressItem[];
  percent: number;
  total: number;
  done: number;
}

/* Sciezki rejestracji/onboardingu — pasek konfiguracji nie ma tam czego szukac. */
const SIGNUP_FLOW_ROUTES = ["/register", "/checkout"] as const;

export function OnboardingProgressBar(): JSX.Element | null {
  const { isAuthenticated, user } = useAuth();
  const [location, setLocation] = useLocation();
  const [expanded, setExpanded] = useState(false);
  const [hidden, setHidden] = useState(() => isOnboardingBarHidden());

  useEffect(() => {
    const sync = (): void => setHidden(isOnboardingBarHidden());
    window.addEventListener(ONBOARDING_BAR_PREF_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(ONBOARDING_BAR_PREF_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const { data } = useQuery<ProgressResponse>({
    queryKey: ["onboarding-progress"],
    queryFn: () => apiRequest("GET", "/api/onboarding/progress"),
    enabled: isAuthenticated,
    staleTime: 60_000,
    retry: false,
  });

  // Track which items were already done so we can fire confetti on transitions.
  // Skip the very first response (already-completed items shouldn't all pop).
  const seenDoneRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!data) return;
    const currentDone = new Set(data.items.filter((i) => i.done).map((i) => i.id));
    if (seenDoneRef.current === null) {
      seenDoneRef.current = currentDone;
      return;
    }
    const newlyDone: string[] = [];
    for (const id of currentDone) {
      if (!seenDoneRef.current.has(id)) newlyDone.push(id);
    }
    if (newlyDone.length > 0) {
      confetti({
        particleCount: 120,
        spread: 80,
        origin: { y: 0.2 },
        colors: ["#ef3e29", "#ff7a52", "#ffffff", "#10b981"],
      });
    }
    seenDoneRef.current = currentDone;
  }, [data]);

  if (SIGNUP_FLOW_ROUTES.some((route) => location.startsWith(route))) return null;
  if (!isAuthenticated || !user || !data || hidden) return null;
  // Hide once everything is done — bar served its purpose, no need to nag.
  if (data.percent >= 100) return null;

  const handleDismiss = (): void => {
    localStorage.setItem(ONBOARDING_BAR_HIDDEN_KEY, "1");
    window.dispatchEvent(new Event(ONBOARDING_BAR_PREF_EVENT));
    setHidden(true);
    setLocation("/settings?tab=account&focus=onboardingBar");
  };

  const goTo = (link?: string): void => {
    if (!link) return;
    if (link.startsWith("http")) {
      window.location.href = link;
    } else {
      setLocation(link);
    }
  };

  const remaining = data.items.filter((i) => !i.done);
  const completed = data.items.filter((i) => i.done);

  return (
    <div className="relative w-full pl-72 lg:pl-80 bg-gradient-to-r from-[#ef3e29]/[0.08] via-[#ef3e29]/[0.04] to-transparent border-b border-[#ef3e29]/[0.18] z-40">
      <div className="px-4 py-2 flex items-center gap-3 text-xs">
        <Sparkles className="w-3.5 h-3.5 text-[#ef3e29] flex-shrink-0" />
        <span className="text-white/80 font-semibold">Konfiguracja Viral Partners</span>
        <div className="flex-1 max-w-md flex items-center gap-2">
          <div className="flex-1 h-2 rounded-full bg-white/[0.08] overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#ef3e29] to-[#ff7a52] transition-all"
              style={{ width: `${data.percent}%` }}
            />
          </div>
          <span className="text-white/70 font-mono text-[11px] flex-shrink-0">
            {data.done}/{data.total} · {data.percent}%
          </span>
        </div>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-[11px] text-[#ef3e29] hover:text-[#d63520] font-medium flex items-center gap-1 flex-shrink-0"
        >
          {expanded ? (
            <>
              Zwiń <ChevronUp className="w-3 h-3" />
            </>
          ) : (
            <>
              Pokaż listę <ChevronDown className="w-3 h-3" />
            </>
          )}
        </button>
        <button
          onClick={handleDismiss}
          className="text-white/30 hover:text-white/60 flex-shrink-0"
          title="Ukryj (możesz włączyć ponownie w ustawieniach konta)"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      {expanded && (
        <>
          <div
            className="fixed inset-0 top-[44px] bg-black/40 backdrop-blur-[2px] z-40"
            onClick={() => setExpanded(false)}
          />
          <div className="absolute left-72 lg:left-80 right-0 top-full bg-[#0c0e14]/98 backdrop-blur-md border-b border-[#ef3e29]/25 shadow-2xl shadow-black/60 z-50 max-h-[70vh] overflow-y-auto">
            <div className="px-5 py-4 space-y-4">
              {remaining.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Circle className="w-3 h-3 text-[#ef3e29]" />
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-white/50">
                      Do zrobienia · {remaining.length}
                    </span>
                  </div>
                  <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {remaining.map((it) => (
                      <li key={it.id}>
                        <button
                          onClick={() => {
                            goTo(it.link);
                            setExpanded(false);
                          }}
                          disabled={!it.link}
                          className="group w-full h-full flex items-start gap-2.5 px-3 py-2.5 rounded-lg border bg-white/[0.03] border-white/[0.08] hover:border-[#ef3e29]/50 hover:bg-[#ef3e29]/[0.06] text-white/85 cursor-pointer text-left transition-all"
                        >
                          <Circle className="w-3.5 h-3.5 text-white/30 group-hover:text-[#ef3e29] mt-0.5 flex-shrink-0 transition-colors" />
                          <span className="flex-1 min-w-0">
                            <span className="block text-[11px] font-medium leading-tight">
                              {it.label}
                            </span>
                            {it.hint && (
                              <span className="block text-[10px] text-white/40 mt-1 leading-tight">
                                {it.hint}
                              </span>
                            )}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {completed.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-white/50">
                      Zrobione · {completed.length}
                    </span>
                  </div>
                  <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1.5">
                    {completed.map((it) => (
                      <li
                        key={it.id}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/[0.04] border border-emerald-500/15"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400/80 flex-shrink-0" />
                        <span className="text-[11px] text-emerald-300/70 line-through truncate">
                          {it.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
